import eventBus from '@/services/eventBus';
import { UAParser } from 'ua-parser-js';
import { useUserStore } from '@/stores/user';
import { useAppStore } from '@/stores/app';
import { useLeagueStore } from '@/stores/league';
import { useTeamStore } from '@/stores/team';
import { useEventStore } from '@/stores/event';
import authService from '@/services/authService';
import { EventFeedType, EventName, Platform } from '@/constants';
import { feedService } from '@/services/feedService';
import { userService } from '@/services/userService';
import { globals } from '@/globals';
import { successMessage } from '@/helpers/message';
import { getWebSocketURL, pageReload } from '@/helpers';
import { setRemoteDictionaries } from '@/helpers/dictionary';
import { getPlatform } from '@/helpers/getPlatform';
import { OrderFactory } from '@/models/orderFactory';

interface State {
    socket: {
        isConnected: boolean;
        message: string;
        reconnectError: boolean;
    };
    userData: {};
    timerId: any;
    lastPongAt: Date;
    lastWsCheckedAt: Date;
    // heartBeatInterval: number;
    // heartBeatTimer: number;
}

export const useWebSocketStore = defineStore('webSocket', {
    state: (): State => ({
        socket: {
            isConnected: false,
            message: '',
            reconnectError: false,
        },
        userData: {},
        timerId: null,
        lastPongAt: new Date(),
        lastWsCheckedAt: new Date(),
    }),
    actions: {
        SOCKET_ONOPEN(event: any) {
            this.socket.isConnected = true;
            console.log('The websocket is ready to use');
            //no need to use globalProperties for socket, it will be issue with vite HMR updates
            //app.config.globalProperties.$socket = event.currentTarget;
            globals.$socket = event.currentTarget;

            const parser = new UAParser(window.navigator.userAgent);

            let data = {
                browser: parser.getBrowser().name + ' ' + parser.getBrowser().version,
                os: parser.getOS().name,
                ...this.userData,
                //pageUrl: window.location.toString(),
            };

            this.sendObj({
                event: 'updateUserData',
                data: data,
            });

            if (getPlatform() !== Platform.Tradeball) {
                //for WL after success login we need to send "appReady", better to do it after socket connected
                let message = JSON.stringify({
                    type: 'appReady',
                });

                console.log('Send msg to parent: ' + message);
                window.parent.postMessage(message, '*');
            }

            this.setLastPongAt(new Date());
            this.heartbeat();
        },

        SOCKET_ONCLOSE() {
            console.log('The websocket is closed');

            this.socket.isConnected = false;

            if (this.timerId) {
                clearInterval(this.timerId);
                console.log('Cleared setInterval in onclose #' + this.timerId);
                this.timerId = null;
            }
        },

        SOCKET_ONERROR(event: any) {
            console.error(this, event);

            this.socket.isConnected = false;

            if (this.timerId) {
                clearInterval(this.timerId);
                console.log('Cleared setInterval in onerror #' + this.timerId);
                this.timerId = null;
            }
        },

        SOCKET_ONMESSAGE(message: any) {
            const appStore = useAppStore();

            switch (message.event) {
                case 'init': {
                    const userStore = useUserStore();
                    const appStore = useAppStore();
                    console.log('webSocket init: ' + message.data.message);

                    appStore.setTimeDiff(message.data.serverTime);
                    if (!message.data.isLogin && userStore.login) {
                        console.log('Your session is expired');
                        authService.logout();
                    }

                    break;
                }
                case 'pong':
                    this.setLastPongAt(new Date());
                    break;
                case 'feedLiveRows': {
                    feedService.wsProcessLive(message.data);
                    break;
                }
                case 'feedDballRows': {
                    if (message.data.marketId === EventFeedType.Today) {
                        feedService.wsProcessToday(message.data);
                    } else if (message.data.marketId === EventFeedType.Early) {
                        feedService.wsProcessEarly(message.data);
                    }
                    break;
                }
                case 'reload':
                    if (message.data.redirectToMain === 'true') {
                        /*router
                            .push({
                                name: 'feedLive',
                            })
                            .catch(() => reload())
                            .then(() => reload());*/
                    } else {
                        // window.location.reload();
                        pageReload();
                    }
                    break;
                case 'logout':
                    authService.logout();
                    break;
                case 'information':
                    successMessage(message.data.message ? message.data.message : message.data);
                    break;
                case 'userBalance': {
                    const userStore = useUserStore();
                    // eventBus.emit('updateUserBalance', message.data.balance);
                    userStore.setBalance(message.data.balance);
                    break;
                }
                case 'userBotStats':
                    // this.commit('BotModule/setStats', message.data.stats);
                    // eventBus.emit('updateUserBotStats', message.data.stats);
                    break;
                case 'userOpenBets':
                    appStore.setBets(message.data.bets);

                    break;
                case 'userOpenOrders':
                    appStore.setOrders(message.data.orders);

                    break;
                case 'dictionary':
                    setRemoteDictionaries(message.data.dictionary);
                    break;
                case 'betAdded': {
                    appStore.addBet(message.data);
                    break;
                }
                case 'betUpdated': {
                    appStore.updateBet(message.data);
                    break;
                }
                case 'pageReload': {
                    eventBus.emit(EventName.PageReload, { page: message.data.page });
                    break;
                }
                case 'notifications': {
                    appStore.setNotifications(message.data.notification);
                    break;
                }
                case 'favoriteUpdated': {
                    const eventStore = useEventStore();
                    eventStore.setFavoriteEvents(message.data.rows);

                    break;
                }
                case 'favoriteLeagueUpdated': {
                    const leagueStore = useLeagueStore();
                    leagueStore.setFavoriteLeagues(message.data.rows);

                    break;
                }
                case 'favoriteTeamUpdated': {
                    const teamStore = useTeamStore();
                    teamStore.setFavoriteTeams(message.data.rows);

                    break;
                }
                case 'favoriteOrderUpdated': {
                    const userStore = useUserStore();
                    userStore.settings.favorite_game_order_by_id = message.data.orderById;
                    //todo need to check, maybe no need to send API request to save settings
                    void userService.changeSettings(userStore.settings);

                    break;
                }
                case 'updateBettingPopup': {
                    const appStore = useAppStore();

                    appStore.updateBettingPopup(message.data);

                    break;
                }
                case 'updateEventPopup': {
                    const eventStore = useEventStore();
                    eventStore.updateEventPopup(message.data);

                    break;
                }
                case 'orderAdded': {
                    const order = OrderFactory.createFromWS(message.data.row);
                    appStore.addOrder(order);

                    break;
                }
                case 'orderUpdated': {
                    appStore.updateOrder(message.data.row);

                    break;
                }
                // case 'orderClosed': {
                //     var msg = '';
                //     let color = 'greenMsg';
                //
                //     if (message.data.fillPercent === 0) {
                //         msg = store.$i18n.t('betOrder.popup.notFilled', { id: message.data.internal_id });
                //         color = 'lightRedMsg';
                //
                //         let info = utils.bettingInfoForMessage(message.data, true, '');
                //
                //         msg += info.gameInfo ? '<br>' + info.gameInfo : '' + info.betInfo ? '<br>' + info.betInfo : '';
                //     } else {
                //         if (message.data.fillPercent === 100) {
                //             msg = store.$i18n.t('betOrder.popup.fullyFilled', { id: message.data.internal_id });
                //         } else {
                //             msg = store.$i18n.t('betOrder.popup.partialFilled', {
                //                 id: message.data.internal_id,
                //                 percent: message.data.fillPercent,
                //             });
                //             color = '#92c47dcc';
                //         }
                //
                //         if (message.data.odds && message.data.odds_requested) {
                //             let oddsDiff = parseFloat(message.data.odds) - parseFloat(message.data.odds_requested);
                //             if (oddsDiff >= 0.01) {
                //                 msg +=
                //                     '<br>' +
                //                     store.$i18n.t('betOrder.popup.takenBetterOdds', {
                //                         oddsDiff: oddsDiff.toFixed(3),
                //                     });
                //             }
                //         }
                //     }
                //
                //     utils.orderClosedMessage(msg, color);
                //
                //     this.dispatch('AppModule/updateOrder', message.data);
                // }
            }
        },

        // mutations for reconnect methods

        SOCKET_RECONNECT() {
            // console.info(this, count);
            if (this.timerId) {
                clearInterval(this.timerId);
                console.log('Cleared setInterval in reconnect #' + this.timerId);
                this.timerId = null;
            }
        },

        SOCKET_RECONNECT_ERROR() {
            this.socket.reconnectError = true;
        },
        setLastPongAt(lastPongAt: Date) {
            this.lastPongAt = lastPongAt;
            console.log('Set lastPongAt = ' + lastPongAt);
        },
        heartbeat: function () {
            if (this.timerId) {
                clearInterval(this.timerId);
                console.log('Cleared setInterval in heartbeat #' + this.timerId);
                this.timerId = null;
            }
            this.timerId = setInterval(() => {
                let lastPongAt = this.lastPongAt;

                let currentDate = new Date();
                let diff = currentDate.getTime() - lastPongAt.getTime();
                let maxDiff = 60 * 1000;

                if (diff >= maxDiff) {
                    console.log(`The websocket should be closed due to timeout, state is ${globals.$socket.readyState}. Diff = ${diff}, lastPongAt = ${lastPongAt}`);
                    if (globals.$socket && globals.$socket.readyState) {
                        const userStore = useUserStore();
                        globals.$disconnect(); //close websocket

                        if (userStore.isLoggedIn()) {
                            const webSocketUrl = getWebSocketURL(userStore.token!);
                            globals.$connect(webSocketUrl);
                        }
                    }
                } else {
                    console.log('Ping. lastPongAt = ' + lastPongAt);
                    this.sendObj({
                        event: 'ping',
                    });
                }
            }, 15000); // 15 seconds

            console.log('Added setInterval #' + this.timerId);
        },
        sendMessage: function (message: string) {
            if (globals.$socket && globals.$socket.readyState === WebSocket.OPEN) {
                globals.$socket.send(message);
            } else {
                console.log('Unable to send message "' + message + '" due to WS is not connected');
            }
        },
        sendObj: function (obj: object) {
            if (globals.$socket && globals.$socket.readyState === WebSocket.OPEN) {
                // globals.$socket.sendObj(obj);
                globals.$socket.send(JSON.stringify(obj));
            } else {
                console.log('Unable to send message ' + JSON.stringify(obj) + ' due to WS is not connected');
            }
        },
        setUserData(data: any) {
            this.userData = data;
        },
        checkConnection() {
            let currentDate = new Date();
            let diff = (currentDate.getTime() - this.lastWsCheckedAt.getTime()) / 1000;

            if (diff < 5) {
                console.log(`Skip checking WS connection, last check was ${diff} sec ago`);
                return;
            }

            this.lastWsCheckedAt = new Date();

            let wsDiff = currentDate.getTime() - this.lastPongAt.getTime();
            if (globals.$socket && globals.$socket.readyState === WebSocket.OPEN && wsDiff < 60 * 1000) {
                console.log(`WS connections is ok, last pong was ${wsDiff}ms ago`);
            } else {
                const userStore = useUserStore();
                if (!userStore.isLoggedIn()) return;

                console.log(`WS connections is failed, readyState is ${globals.$socket.readyState}, last pong was ${wsDiff}ms ago`);

                globals.$disconnect();
                const webSocketUrl = getWebSocketURL(userStore.token!);
                globals.$connect(webSocketUrl);
            }
        },
    },
});
